import type { Context, Unit } from "./types";
import { unitDetailsLabels } from "./labels.ts";
export type Screen = {
  id: string;
  title: [string, string];
  hint: [string, string];
  reads: string[];
  writes: [string, string, string][];
};
export const screens: Screen[] = [
  {
    id: "structure",
    title: ["تفاصيل المسابقة", "Competition details"],
    hint: [
      "المسابقة تضم مواسم، ويضم كل موسم جولة تصويت واحدة أو أكثر.",
      "A competition contains seasons; each season contains one or more voting rounds.",
    ],
    reads: ["unit", "readiness"],
    writes: [
      ["unitCreate", "إضافة وحدة", "Add unit"],
      ["unitUpdate", "بيانات الوحدة", "Unit details"],
      ["unitMove", "نقل الوحدة", "Move unit"],
      ["unitOrder", "ترتيب الأبناء", "Order children"],
      ["unitLifecycle", "نشر أو أرشفة", "Publish or archive"],
      ["unitDeleteDraft", "حذف مسودة", "Delete draft"],
    ],
  },
  { id: "surveys", title: ["الاستبيانات", "Surveys"], hint: ["استبيانات المسابقة الموروثة", "Inherited competition surveys"], reads: [], writes: [] },
  { id: "roleSetup", title: ["الأدوار", "Roles"], hint: ["أدوار هذا المستوى وفروعه", "Roles in this scope and descendants"], reads: [], writes: [] },
  { id: "nominations", title: ["طلبات الترشح للتحكيم", "Judging nominations"], hint: ["مراجعة طلبات الانضمام", "Review nominations"], reads: [], writes: [] },
  {
    id: "entries",
    title: ["الفرق", "Teams"],
    hint: [
      "أنشئ فريقاً، ثم افتح تفاصيله لإضافة الأعضاء عبر دعوات الانضمام. تُثبت العضوية بعد قبول الدعوة.",
      "Create a team, then open its details to invite members. Membership starts after invitation acceptance.",
    ],
    reads: ["groups", "entryCandidates"],
    writes: [
      ["groupCreate", "إنشاء فريق", "Create team"],
      ["groupMember", "عضوية الفريق", "Team membership"],
      ["entryUpdate", "بيانات المشاركة", "Entry details"],
      ["entryRoster", "تشكيل المشاركة", "Entry roster"],
      ["entryMemberPolicy", "صلاحيات التشكيل", "Roster permissions"],
      ["entryMedia", "وسائط المشاركة", "Entry media"],
      ["entryLifecycle", "تفعيل أو انسحاب", "Activate or withdraw"],
      ["roundEntry", "إدراج في الجولة", "Round admission"],
      ["entryPromote", "تأهيل لجولة أخرى", "Promote entries"],
    ],
  },
  {
    id: "roles",
    title: ["الأدوار والتفويض", "Roles & delegation"],
    hint: [
      "صلاحيات إدارة الوحدة مستقلة عن أدوار التصويت. لا يمكن تفويض صلاحية تتجاوز صلاحية المدير الحالية.",
      "Unit management permissions are separate from voting roles. A manager can only delegate permissions they currently hold.",
    ],
    reads: ["roleDefinitions", "roles", "admins"],
    writes: [
      ["roleGrant", "إسناد دور", "Assign role"],
      ["roleRevoke", "سحب دور", "Revoke role"],
      ["adminGrant", "تفويض إدارة الموسم", "Delegate unit management"],
      ["adminRevoke", "سحب تفويض", "Revoke delegation"],
    ],
  },
  {
    id: "policies",
    title: ["إعداد التصويت", "Voting setup"],
    hint: [
      "اختر الأدوار وأوزانها، ثم حدّد التصويت المباشر وعدد الأصوات أو استبياناً من استبيانات المسابقة. تثبت الإعدادات عند تجهيز الموسم.",
      "Choose roles and weights, then direct voting with a vote limit or an inherited competition survey. Settings freeze when the season becomes ready.",
    ],
    reads: ["votingPolicy", "evaluationForm", "readiness"],
    writes: [
      ["votingPolicy", "سياسة التصويت", "Voting policy"],
      ["evaluationForm", "نموذج التقييم", "Evaluation form"],
    ],
  },
  {
    id: "live",
    title: ["إدارة التصويت", "Run voting"],
    hint: [
      "جهّز الجولة، افتحها، أغلقها ثم اختم مدخلاتها قبل الاحتساب. في العرض الفردي تُفتح نافذة تقييم محددة للفريق والأدوار.",
      "Prepare, open, close and seal the round before computing results. Presentation windows restrict evaluation to the selected team and roles.",
    ],
    reads: ["roundLive", "readiness"],
    writes: [
      ["roundSchedule", "جدولة الجولة", "Round schedule"],
      ["roundLifecycle", "تغيير حالة الجولة", "Change round state"],
      ["roundPresentation", "نافذة عرض فريق", "Team presentation window"],
    ],
  },
  {
    id: "results",
    title: ["النتائج", "Results"],
    hint: [
      "حدد المصادر وأوزانها، وقواعد التعادل والغياب والتقريب. تابع مهمة الاحتساب، راجع اللقطة، ثم انشرها. لا تُنشر النتيجة تلقائياً.",
      "Set source weights, ties, missing entries and rounding. Track the computation, review the snapshot, then publish it. Publication is a separate action.",
    ],
    reads: ["resultPolicy", "resultSnapshots", "result", "resultJob"],
    writes: [
      ["resultPolicy", "إعداد النتيجة", "Result policy"],
      ["resultCompute", "احتساب النتيجة", "Compute result"],
      ["resultPublish", "نشر نسخة النتائج", "Publish snapshot"],
      ["resultUnpublish", "سحب النشر", "Unpublish result"],
    ],
  },
  {
    id: "audit",
    title: ["سجل الفعالية", "Event audit"],
    hint: [
      "راجع الإجراءات ضمن الوحدة المحددة، مع ترشيح الإجراء والفترة والتنقل بين الصفحات.",
      "Review actions for the selected unit, filter by action or date and page through the history.",
    ],
    reads: ["audit"],
    writes: [],
  },
];
export const readTitles: Record<string, [string, string]> = {
  unit: ["بيانات الوحدة", "Unit details"],
  readiness: ["فحص الجاهزية", "Readiness"],
  groups: ["الفرق المشاركة", "Participating teams"],
  entryCandidates: ["فرق قابلة للمشاركة", "Eligible teams"],
  entries: ["المشاركات", "Entries"],
  entry: ["تفاصيل مشاركة", "Entry details"],
  roleDefinitions: ["الأدوار المتاحة", "Available roles"],
  roles: ["الأدوار المسندة", "Assigned roles"],
  admins: ["مديرو الوحدة", "Unit managers"],
  votingPolicy: ["سياسة التصويت الحالية", "Current voting policy"],
  evaluationForm: ["نموذج التقييم الحالي", "Current evaluation form"],
  roundLive: ["حالة الجولة المباشرة", "Live round status"],
  resultPolicy: ["سياسة النتيجة الحالية", "Current result policy"],
  resultSnapshots: ["نسخ النتائج المحفوظة", "Result snapshots"],
  result: ["جدول النتائج", "Result table"],
  resultJob: ["متابعة الاحتساب", "Computation status"],
  audit: ["سجل الإجراءات", "Activity log"],
  access: ["حالة مساحة الجهة", "Workspace status"],
  productPolicy: ["سياسة المنتج الحالية", "Current product policy"],
  legacyMigrationPlan: [
    "معاينة ترحيل المسابقة القديمة",
    "Legacy migration preview",
  ],
};

/** Navigation reflects the fixed hierarchy; server authorization remains authoritative. */
export function canCreateChild(unit: Unit | null): boolean {
  return !unit || (["COMPETITION", "STAGE"].includes(unit.unitKind) && unit.status === "DRAFT");
}
export function contextScreens(context: Context): Screen[] {
  const unit = context.unit;
  const byId = (id: string) => screens.find(s => s.id === id)!;
  const structure = byId("structure");
  const round = unit?.unitKind === "VOTING_ROUND";
  const season = unit?.unitKind === "STAGE";
  const result = !!unit && ["STAGE", "VOTING_ROUND", "RESULT"].includes(unit.unitKind);
  const additional = [...(round ? [byId("policies"), byId("live")] : []), ...(result && !season ? [byId("results")] : []), ...(unit ? [byId("audit")] : [])];
  const merged: Screen[] = [{ ...structure, reads: [...new Set([...structure.reads, ...additional.flatMap(s => s.reads)])], writes: [...structure.writes, ...additional.flatMap(s => s.writes)] },
    ...(unit ? [{ ...byId("roleSetup"), reads: byId("roles").reads.filter(name => name !== "roleDefinitions"), writes: byId("roles").writes }] : []),
    ...(unit && unit.unitKind !== "COMPETITION" ? [byId("surveys"), byId("entries"), byId("nominations")] : []),
    ...(season ? [byId("results")] : [])];
  return merged.filter(screen => unit?.unitKind !== "RESULT" || screen.id === "structure").map(screen => ({ ...screen,
    title: screen.id === "structure" && unit ? [unitDetailsLabels(unit.unitKind, "ar").tab, unitDetailsLabels(unit.unitKind, "en").tab] : screen.title,
    reads: screen.reads.filter(name => {
    if (!unit) return false;
    if (season && screen.id === "entries") return name === "groups";
    if (["entryCandidates", "entries", "entry"].includes(name)) return !!unit.registrationScopeId;
    return true;
  }), writes: screen.writes.filter(([name]) => {
    if (context.readOnly || unit?.unitKind === "RESULT") return false;
    if (name === "unitCreate") return canCreateChild(unit);
    if (!unit) return false;
    if (name === "unitDeleteDraft") return unit.status === "DRAFT";
    if (name === "unitMove") return !!unit.parentUnitId;
    if (name === "unitOrder") return ["COMPETITION", "STAGE"].includes(unit.unitKind);
    if (["roundEntry", "entryPromote"].includes(name)) return round;
    if (name.startsWith("entry")) return !!unit.registrationScopeId;
    if (["votingPolicy", "evaluationForm"].includes(name)) return !unit.roundState || unit.roundState === "DRAFT";
    return true;
  }) }));
}

export const primaryActions: Record<string, string[]> = {
  structure: ["unitUpdate"],
  entries: ["groupCreate", "roundEntry"],
  roleSetup: ["roleGrant", "adminGrant"],
  policies: [],
  live: ["roundLifecycle", "roundSchedule"],
  results: ["resultPolicy", "resultCompute"],
  audit: [],
};

export function initialRead(screen: Screen, _unit: Unit | null): string {
  const preferred: Record<string, string> = { entries: "groups",
    roles: "roles", live: "roundLive", results: "resultPolicy", audit: "audit" };
  return screen.reads.includes(preferred[screen.id]) ? preferred[screen.id] : "";
}
